import React from "react";
import { Box, Theme, Typography } from "@mui/material";
import { Audience, Block } from "@tiny/icons";
import {
  AudienceMembershipView,
  SuppressionMembershipView,
} from "../../app/GeneratedApi";
import { TableHeaderCellsTypes } from "../../components/Table/table.types";

export const SEGMENTS_PAGE_SIZE = 5;

export interface SegmentTableRow {
  segmentId: string;
  rawName: string;
  segmentName: React.ReactElement;
  segmentTypeLabel: React.ReactElement;
  isRemovable: boolean;
}

export interface SuppressionTableRow {
  suppressionId: number;
  suppressionName: React.ReactElement;
  suppressionScopeLabel: React.ReactElement;
}

export const segmentHeaderCells: TableHeaderCellsTypes<SegmentTableRow>[] = [
  { propName: "segmentName", label: "Audience", isSortable: false },
  {
    propName: "segmentTypeLabel",
    label: "Type",
    isSortable: false,
    width: "160px",
  },
];

export const suppressionHeaderCells: TableHeaderCellsTypes<SuppressionTableRow>[] =
  [
    {
      propName: "suppressionName",
      label: "Suppression list",
      isSortable: false,
    },
    {
      propName: "suppressionScopeLabel",
      label: "Scope",
      isSortable: false,
      width: "160px",
    },
  ];

function Pill({
  label,
  getColors,
}: {
  label: string;
  getColors: (theme: Theme) => {
    bg: string | undefined;
    fg: string | undefined;
  };
}) {
  return (
    <Box
      component="span"
      sx={(theme) => {
        const { bg, fg } = getColors(theme);
        return {
          display: "inline-flex",
          alignItems: "center",
          height: 20,
          px: theme.spacing(2),
          borderRadius: 9999,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.02em",
          backgroundColor: bg,
          color: fg,
          whiteSpace: "nowrap",
        };
      }}
    >
      {label}
    </Box>
  );
}

function SegmentNameCell({ name }: { name: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          borderRadius: 1.5,
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.primary[20],
          color: theme.palette.primary.main,
        })}
      >
        <Audience sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        variant="label2"
        sx={{
          color: "primary.main",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

function SuppressionNameCell({ name }: { name: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          borderRadius: 1.5,
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.red[50],
          color: theme.palette.error.main,
        })}
      >
        <Block sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        variant="label2"
        color="grey.100"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

export function segmentTypePill(
  segmentType: "UPLOAD" | "INTEGRATION" | "FORM"
) {
  if (segmentType === "UPLOAD") {
    return (
      <Pill
        label="Manual"
        getColors={(theme) => ({
          bg: theme.palette.primary[20],
          fg: theme.palette.primary.main,
        })}
      />
    );
  }
  return (
    <Pill
      label="Integration"
      getColors={(theme) => ({
        bg: theme.palette.purple[50],
        fg: theme.palette.purple[125],
      })}
    />
  );
}

function suppressionScopePill(scope: "LOCAL" | "GLOBAL") {
  if (scope === "GLOBAL") {
    return (
      <Pill
        label="GLOBAL"
        getColors={(theme) => ({
          bg: theme.palette.red[50],
          fg: theme.palette.error.main,
        })}
      />
    );
  }
  return (
    <Pill
      label="LOCAL"
      getColors={(theme) => ({
        bg: theme.palette.grey[30],
        fg: theme.palette.grey[90],
      })}
    />
  );
}

export function transformSegmentsToRows(
  audiences: AudienceMembershipView[]
): SegmentTableRow[] {
  return audiences.map((audience) => ({
    segmentId: audience.segmentId,
    rawName: audience.segmentName,
    segmentName: <SegmentNameCell name={audience.segmentName} />,
    segmentTypeLabel: segmentTypePill(audience.segmentType),
    isRemovable: audience.segmentType === "UPLOAD",
  }));
}

export function transformSuppressionsToRows(
  suppressions: SuppressionMembershipView[]
): SuppressionTableRow[] {
  return suppressions.map((suppression) => ({
    suppressionId: suppression.suppressionId,
    suppressionName: <SuppressionNameCell name={suppression.suppressionName} />,
    suppressionScopeLabel: suppressionScopePill(suppression.suppressionScope),
  }));
}
